import { useMemo } from 'react';

import { useImageSliderData } from '~/src/common/components/ImageSlider/useImageSliderData';
import { CartProduct } from '~/src/common/typings/cart';
import { isTruthy } from '~/src/common/utils/guards';
import { GetCategories200 } from '~/src/queries/api-ecom/generated/api-ecom.schemas';
import { RecipeXSellCategory } from '~/src/screens/CatalogContent/ContentScreen/RecipeScreen/components/RecipeXSellCategory/RecipeXSellCategory';
import { CatalogContentBreadcrumb } from '~/src/screens/CatalogContent/ContentScreen/shared/CatalogContentBreadcrumb/CatalogContentBreadcrumb';

import EssentialsPanel from './components/EssentialsPanel/EssentialsPanel';
import IngredientsPanel from './components/IngredientsPanel/IngredientsPanel';
import RecipeDescription from './components/RecipeDescription/RecipeDescription';
import RecipeIngredients from './components/RecipeIngredients/RecipeIngredients';
import RecipePreparation from './components/RecipeSteps/RecipeSteps';
import useAddAllArticles from './hooks/useAddAllArticles';
import useRecipeAnalytics from './hooks/useRecipeAnalytics';
import useRecipeIngredients from './hooks/useRecipeIngredients';
import { ChangeServingsAnalyticsInfo } from './hooks/useServings';
import * as S from './layout';
import { SeoRecipe } from './SeoRecipe';
import { Recipe, RecipeIngredientProductArticle } from './types';
import computeTotalPrice from './utils/computeTotalPrice';
import isMissingQuantity from './utils/isMissingQuantity';

interface Props {
  recipe: Recipe;
  servings: number;
  categories?: GetCategories200;
  onChangeServings: (servings: number, info: ChangeServingsAnalyticsInfo) => void;
}

const RecipeContent = ({ recipe, servings, onChangeServings }: Props) => {
  const { id, description, descriptionTitle, steps, ingredients: basicIngredients, slugs } = recipe;

  // Les recettes peuvent avoir plusieurs images, mais on n'utilise que la 1ere
  const sliderImages = useImageSliderData(recipe.images.slice(0, 1));

  const {
    ingredients,
    essentials,
    handleReplaceIngredient,
    handleUpdateIngredientQuantity,
    isLoading,
  } = useRecipeIngredients(slugs[0], servings);

  const ingredientsArticles = ingredients.map(i => i.article).filter(isTruthy);

  const { analyticsProperties } = useRecipeAnalytics({ recipe, ingredientsArticles });

  const { addAllArticles, isLoading: isAdding } = useAddAllArticles({
    ingredients,
    onSuccess: (articles: RecipeIngredientProductArticle[], cartProducts: CartProduct[]) => {
      articles.forEach(article => {
        if (
          isMissingQuantity({
            article,
            cartProducts,
          })
        ) {
          handleUpdateIngredientQuantity(article.id, article.quantity);
        }
      });
    },
    servings,
  });

  const pricePerServing = useMemo(
    () => (isLoading ? undefined : computeTotalPrice(ingredientsArticles) / servings),
    [ingredientsArticles, servings, isLoading],
  );

  return (
    <>
      <SeoRecipe recipe={recipe} />
      <S.Root>
        <CatalogContentBreadcrumb item={recipe} analyticsProperties={analyticsProperties} />
        <S.Content>
          <S.Grid>
            <S.RecipeImageSlider images={sliderImages} title={recipe.name} hideThumbnails />
            <S.NameAndTags recipe={recipe} />
            <S.PreparationInfos recipe={recipe} pricePerServing={pricePerServing} />
            <S.AddToCart
              recipe={recipe}
              ingredients={ingredients}
              servings={servings}
              isAdding={isAdding}
              onChangeServings={onChangeServings}
              onAddAllArticles={addAllArticles}
            />
            {recipe.shortDescription ? (
              <S.ShortDescription shortDescription={recipe.shortDescription} />
            ) : null}
          </S.Grid>
          <S.IngredientsAndEssentials>
            <RecipeIngredients ingredients={basicIngredients} servings={servings} />
            <IngredientsPanel
              ingredients={ingredients}
              onChangeServings={onChangeServings}
              onReplace={handleReplaceIngredient}
              servings={servings}
              onUpdateIngredientQuantity={handleUpdateIngredientQuantity}
              onAddAllArticles={addAllArticles}
              isLoading={isAdding}
            />
            {essentials.length ? (
              <EssentialsPanel essentialProducts={essentials} recipeId={id} />
            ) : null}
          </S.IngredientsAndEssentials>
          <RecipePreparation gears={recipe.gears} steps={steps} />
          <RecipeDescription description={description} descripionTitle={descriptionTitle} />
          <RecipeXSellCategory />
        </S.Content>
      </S.Root>
    </>
  );
};

export default RecipeContent;
